/**
 * useGoLive Hook
 *
 * Manages the "Go Live" / activation flow extracted from GoLiveC1.
 *
 * Responsibilities:
 * - Build onboarding request from form data
 * - Manage state machine (ready → processing → success | error)
 * - Show progressive processing steps
 * - Expose onboarding result (credentials, org ID, etc.)
 */

import { useState, useCallback } from 'react';
import { onboardC1Organization } from '../services/c1OnboardingService';
import { getPortalUrl } from '../config/runtime';
import {
  C1OnboardingRequest,
  C1OnboardingResponse,
  C1OnboardingError,
  SubscriptionTier,
  PartyType,
} from '../types/c1-onboarding.types';
import { BILLING_PLANS } from '../config/billingConfig';

// Re-export for consumers
export type { SubscriptionTier, PartyType };

export type OnboardingState = 'ready' | 'processing' | 'success' | 'error';

export interface OnboardingResult {
  success: boolean;
  organizationId?: string;
  userId?: string;
  loginId?: string;
  email?: string;
  temporaryPassword?: string;
  loginUrl?: string;
  error?: string;
}

export interface GoLiveFormData {
  partyName?: string;
  partyAbn?: string;
  partyAcn?: string;
  partyType?: string;
  partyEntityType?: string;
  partyEmail?: string;
  partyAddress?: string;
  partySector?: string;
  adminPhone?: string;
  adminFirstName?: string;
  adminLastName?: string;
  stripeBillingEmail?: string;
  stripePaymentMethodId?: string;
  stripePlanType?: string;
  creditsPurchased?: number;
  agreedToTerms?: boolean;
  signedAgreementPdfS3Key?: string;
  signedAgreementPdfBase64?: string;
}

/** Map party/entity type to API format */
export const mapPartyType = (type: string | undefined): PartyType => {
  if (!type) return 'company';
  const lower = type.toLowerCase();

  const legacyMapping: Record<string, PartyType> = {
    'sole-traders': 'individual',
    'partnerships-or-associations': 'partnership',
    'body-corporates-pty': 'company',
    'trusts': 'trust',
    'government-bodies': 'company',
  };
  if (legacyMapping[type]) return legacyMapping[type];

  if (lower.includes('trust')) return 'trust';
  if (lower.includes('individual') || lower.includes('sole trader')) return 'individual';
  if (lower.includes('partnership') || lower.includes('association')) return 'partnership';
  return 'company';
};

/** Map billing plan to subscription tier */
export const mapPlanToTier = (plan: string | undefined): SubscriptionTier => {
  const mapping: Record<string, SubscriptionTier> = {
    STARTER: 'starter',
    GROWTH: 'growth',
    ENTERPRISE: 'enterprise',
    OFFER: 'offer',
  };
  return mapping[plan || ''] || 'starter';
};

export interface UseGoLiveReturn {
  /** Current state of the activation flow */
  onboardingState: OnboardingState;
  /** Activation result (credentials, error, etc.) */
  onboardingResult: OnboardingResult | null;
  /** Current processing step label */
  processingStep: string;
  /** Start the activation process */
  handleGoLive: () => Promise<void>;
  /** Retry after an error */
  handleRetry: () => void;
  /** Get the credits count for display */
  getCredits: () => number;
}

/** Parse a compact AU address string (e.g. "12 Main St SA 5008") into components */
function parseAuAddress(raw: string) {
  const stateMatch = raw.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
  const postcodeMatch = raw.match(/\b(\d{4})\b/);
  const state = stateMatch ? stateMatch[1].toUpperCase() : '';
  const postcode = postcodeMatch ? postcodeMatch[1] : '';
  const street = raw
    .replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i, '')
    .replace(/\b\d{4}\b/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { street, state, postcode };
}

export function useGoLive(data: GoLiveFormData): UseGoLiveReturn {
  const [onboardingState, setOnboardingState] = useState<OnboardingState>('ready');
  const [onboardingResult, setOnboardingResult] = useState<OnboardingResult | null>(null);
  const [processingStep, setProcessingStep] = useState('');

  const getCredits = useCallback(() => {
    return data.creditsPurchased || (BILLING_PLANS[data.stripePlanType as keyof typeof BILLING_PLANS]?.credits ?? 40);
  }, [data.creditsPurchased, data.stripePlanType]);

  const handleGoLive = useCallback(async () => {
    setOnboardingState('processing');
    setProcessingStep('Creating your organization...');

    try {
      const request: C1OnboardingRequest = {
        organization: {
          partyName: data.partyName || '',
          partyAbn: (data.partyAbn || '').replace(/\s/g, ''),
          partyAcn: (data.partyAcn || '').replace(/\s/g, ''),
          partyType: mapPartyType(data.partyEntityType || data.partyType),
          contactEmail: data.partyEmail || data.stripeBillingEmail || '',
          contactPerson: data.partyName || '',
          contactPhone: data.adminPhone || '',
          address: data.partyAddress
            ? { ...parseAuAddress(data.partyAddress), city: '', country: 'AU' }
            : undefined,
          partySector: data.partySector || '',
          partyEntityType: data.partyEntityType || '',
          partyAddress: data.partyAddress || '',
        },
        primaryUser: {
          email: data.partyEmail || data.stripeBillingEmail || '',
          firstName: data.adminFirstName || 'Admin',
          lastName: data.adminLastName || 'User',
          phone: data.adminPhone || '',
          role: 'admin',
        },
        subscription: {
          planId: mapPlanToTier(data.stripePlanType),
          planType: mapPlanToTier(data.stripePlanType),
          billingInterval: 'monthly',
          paymentMethodId: data.stripePaymentMethodId,
          initialCredits: getCredits(),
        },
        agreementSignedAt: new Date().toISOString(),
        agreementSignedBy: data.partyName || '',
        agreementVersion: '2024.1',
        termsAccepted: data.agreedToTerms || true,
        privacyAccepted: data.agreedToTerms || true,
        signedAgreementPdf: data.signedAgreementPdfS3Key || data.signedAgreementPdfBase64,
      };

      // Progressive status steps
      setProcessingStep('Creating your organization...');
      await new Promise((resolve) => setTimeout(resolve, 800));
      setProcessingStep('Setting up your user account...');
      await new Promise((resolve) => setTimeout(resolve, 600));
      setProcessingStep('Configuring your subscription...');
      await new Promise((resolve) => setTimeout(resolve, 600));
      setProcessingStep('Provisioning account resources...');
      await new Promise((resolve) => setTimeout(resolve, 500));
      setProcessingStep('Finalizing account setup...');

      const result = await onboardC1Organization(request);

      if (result.success) {
        const successResult = result as C1OnboardingResponse;
        setProcessingStep('Sending welcome email...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        setOnboardingResult({
          success: true,
          organizationId: successResult.organizationId,
          userId: successResult.userId,
          loginId: successResult.credentials.loginId,
          email: successResult.credentials.email,
          temporaryPassword: successResult.credentials.temporaryPassword,
          loginUrl: successResult.loginUrl || `${getPortalUrl()}/login`,
        });
        setOnboardingState('success');
        localStorage.removeItem('c1_signup_form_data');
      } else {
        const errorResult = result as C1OnboardingError;
        setOnboardingResult({
          success: false,
          error: errorResult.error?.message || 'Account setup failed. Please try again.',
        });
        setOnboardingState('error');
      }
    } catch (err) {
      setOnboardingResult({
        success: false,
        error: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
      setOnboardingState('error');
    }
  }, [data, getCredits]);

  const handleRetry = useCallback(() => {
    setOnboardingState('ready');
    setOnboardingResult(null);
    setProcessingStep('');
  }, []);

  return {
    onboardingState,
    onboardingResult,
    processingStep,
    handleGoLive,
    handleRetry,
    getCredits,
  };
}
